import { useRouter } from 'next/router';
import { MutableRefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import useRefState, { RefState } from './useRefState';

const querystringToDate = (queryTimestamp: string | string[] | undefined): Date | undefined => {
  if (!queryTimestamp) return undefined;

  return new Date(Number(queryTimestamp));
};

export type CurrentTimestamp = {
  currentTimestampRef: RefState<Date | undefined>;
  setCurrentTimestamp: (arg0: Date | undefined) => void;
  updateQueryStringTimestamp: MutableRefObject<(overRideTimestamp?: Date) => void>;
};

const useCurrentTimestamp = (): CurrentTimestamp => {
  const router = useRouter();

  const { query } = router;

  const [currentTimestampRef, setCurrentTimestamp] = useRefState(
    querystringToDate(query.currentTimestamp),
  );

  const updateQueryStringTimestamp = useCallback(() => {
    const timestamp = currentTimestampRef.current.value;
    if (!timestamp) return;
    router.replace(
      {
        query: {
          ...router.query,
          currentTimestamp: `${timestamp?.getTime()}`,
        },
      },
      undefined,
      { shallow: true },
    );
  }, [currentTimestampRef, router]);

  // has to be a ref because router updates too frequently
  const updateQueryStringTimestampRef = useRef(updateQueryStringTimestamp);

  useEffect(() => {
    updateQueryStringTimestampRef.current = updateQueryStringTimestamp;
  }, [updateQueryStringTimestamp]);

  const currentTimestamp = useMemo(
    () => ({
      currentTimestampRef,
      setCurrentTimestamp,
      updateQueryStringTimestamp: updateQueryStringTimestampRef,
    }),
    [currentTimestampRef, setCurrentTimestamp, updateQueryStringTimestampRef],
  );

  return currentTimestamp;
};

export default useCurrentTimestamp;
